import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calculateRequiredMargin } from "../services/backendAPI";

const DEBOUNCE_MS = 500;

const isMarketOrderType = (orderType) =>
  orderType === "MARKET" || orderType === "STOPLOSSMARKET";

const resolveEffectivePrice = ({ orderType, orderSide, price, bestBid, bestAsk, ltp }) => {
  if (!isMarketOrderType(orderType)) return Number(price) || 0;
  const marketPrice = orderSide === "BUY" ? bestAsk : bestBid;
  return Number(marketPrice) || Number(ltp) || 0;
};

// User-controlled inputs only. For MARKET orders, price comes from market data
// and is intentionally excluded so bid/ask updates do not look like user edits.
const buildUserKey = ({ exchange, token, product, orderSide, orderType, quantity, price }) =>
  JSON.stringify({
    exchange,
    token,
    product,
    orderSide,
    orderType,
    quantity: Number(quantity) || 0,
    price: isMarketOrderType(orderType) ? null : Number(price) || 0,
  });

const useMarginCalculator = (params) => {
  const {
    enabled,
    isOpen,
    exchange,
    product,
    orderSide,
    orderType,
    token,
    quantity,
    price,
    bestBid,
    bestAsk,
    ltp,
  } = params;

  const [requiredMargin, setRequiredMargin] = useState(0);
  const [isAllowed, setIsAllowed] = useState(true);
  const [marginData, setMarginData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStale, setIsStale] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);

  // Latest params snapshot so refresh() can be a stable callback
  const latestRef = useRef(params);
  latestRef.current = params;

  const debounceTimerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const lastFetchedUserKeyRef = useRef(null);
  const lastFetchedBidAskRef = useRef({ bestBid: null, bestAsk: null });

  const refresh = useCallback(async () => {
    const v = latestRef.current;
    if (!v.enabled) return;
    if (isFetchingRef.current) return;

    const effectivePrice = resolveEffectivePrice(v);
    const qty = Number(v.quantity) || 0;
    if (!v.exchange || !v.token || !v.product || !v.orderSide || qty <= 0 || effectivePrice <= 0) {
      return;
    }

    let effectiveProductType = v.product;
    if (v.exchange === "CDS") {
      effectiveProductType = "NRML";
    } else if (v.exchange === "DCSX") {
      effectiveProductType = "CNC";
    }

    const payload = {
      exchange: v.exchange,
      instrument_type: v.instrument_type || "",
      product_type: effectiveProductType,
      side: v.orderSide,
      underlying_token: Number(v.underlying_token) || 0,
      token: Number(v.token),
      trading_symbol: v.trading_symbol,
      quantity: qty,
      price: String(effectivePrice),
      lot_size: Number(v.lot_size) || 1,
    };

    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const response = await calculateRequiredMargin(payload);
      if (response && response.status === "success" && response.data) {
        const data = response.data;
        setRequiredMargin(Number(data.required_margin) || 0);
        setIsAllowed(data.is_allowed !== false);
        setMarginData(data);
        setErrorMessage("");
        setHasApiError(false);
        lastFetchedUserKeyRef.current = buildUserKey(v);
        lastFetchedBidAskRef.current = { bestBid: v.bestBid, bestAsk: v.bestAsk };
        setIsStale(false);
      } else {
        lastFetchedUserKeyRef.current = buildUserKey(v);
        setErrorMessage(response?.message || "Failed to calculate required margin.");
        setIsAllowed(false);
        setHasApiError(true);
      }
    } catch (err) {
      lastFetchedUserKeyRef.current = buildUserKey(v);
      setErrorMessage(
        err?.message || err?.response?.data?.message || "Failed to calculate required margin."
      );
      setIsAllowed(false);
      setHasApiError(true);
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, []);

  // Reset everything when the order window closes
  useEffect(() => {
    if (isOpen) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    isFetchingRef.current = false;
    lastFetchedUserKeyRef.current = null;
    lastFetchedBidAskRef.current = { bestBid: null, bestAsk: null };
    setRequiredMargin(0);
    setIsAllowed(true);
    setMarginData(null);
    setErrorMessage("");
    setHasApiError(false);
    setIsStale(false);
    setIsLoading(false);
  }, [isOpen]);

  const currentUserKey = useMemo(
    () => buildUserKey({ exchange, token, product, orderSide, orderType, quantity, price }),
    [exchange, token, product, orderSide, orderType, quantity, price]
  );

  // True once we have a usable price (user-entered for LIMIT, or first tick for MARKET).
  // Used as a dependency instead of raw bestBid/bestAsk/ltp so the fetch effect
  // does not re-fire on every WebSocket tick.
  const hasValidPrice = useMemo(
    () => resolveEffectivePrice({ orderType, orderSide, price, bestBid, bestAsk, ltp }) > 0,
    [orderType, orderSide, price, bestBid, bestAsk, ltp]
  );

  // First fetch (immediate) + subsequent user-input changes (debounced).
  // Market-data updates (bestBid/bestAsk) deliberately do NOT trigger this path —
  // see the stale effect below.
  useEffect(() => {
    if (!enabled || !isOpen) return;

    if (!exchange || !token || !product || !orderSide || Number(quantity) <= 0 || !hasValidPrice) {
      return;
    }

    if (lastFetchedUserKeyRef.current === null) {
      refresh();
      return;
    }
    if (currentUserKey === lastFetchedUserKeyRef.current) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      refresh();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [
    enabled,
    isOpen,
    currentUserKey,
    exchange,
    token,
    product,
    orderSide,
    orderType,
    quantity,
    price,
    hasValidPrice,
    refresh,
  ]);

  // For MARKET-style orders, flag stale whenever bid/ask drifts away from the
  // values used in the last successful fetch. User must hit refresh CTA.
  useEffect(() => {
    if (!enabled || !isOpen) return;
    if (!isMarketOrderType(orderType)) return;
    const last = lastFetchedBidAskRef.current;
    if (last.bestBid === null && last.bestAsk === null) return;
    if (last.bestBid !== bestBid || last.bestAsk !== bestAsk) {
      setIsStale(true);
    }
  }, [enabled, isOpen, orderType, bestBid, bestAsk]);

  return {
    requiredMargin,
    isAllowed,
    marginData,
    errorMessage,
    isLoading,
    isStale,
    hasApiError,
    refresh,
  };
};

export default useMarginCalculator;